// Admin workspace > "Data maintenance" tab (admin.ts) — status of the
// maintenance scheduler (src/wcc_viewer/maintenance.py: live job status
// refresh + GIS layer cache sweep + data-source sweep), data vintage
// alerts, per-layer / per-source age, cadence and next run, and
// "run now" / "refresh layer" / "refresh or check source" buttons. Backend:
// routers/maintenance.py — platform_admin only since 2026-10-01 (it was any
// client's admin): the tab is hidden for everyone else. The buttons never
// do the work in the web server: they queue a request for the scheduler
// leader, or start a one-off maintenance process if no scheduler is running.
// The same status payload's Office-engine and job-worker parts are shown on
// the Diagnostics tab (diagnosticsUI.ts, via loadMaintenanceStatus).
//
// "Suggested re-runs": each pipeline-behind notice carries the module chain
// that would bring the pipeline up to date (backend: pipeline_reruns.py,
// derived from module_dependencies.py's graph). Approve runs it through the
// normal module-run machinery after an in-page confirm; Dismiss hides it
// until the source's content changes again. Only users the backend marks
// can_approve (tenancy.can_run_shared_modules) get the buttons enabled.
import { getJson, postJson, streamRun } from "./api";
import { el } from "./legend";
import { modalBox, overlay } from "./staleness";
import { showToast } from "./toast";

interface MaintTask {
  name: string;
  enabled: boolean;
  schedule: string;
  running: boolean;
  interrupted: boolean;
  last_started: string | null;
  last_finished: string | null;
  last_status: string | null;
  last_duration_s: number | null;
  last_error: string | null;
  last_result: Record<string, unknown> | null;
  trigger: string | null;
  next_run: string | null;
  leader_supports: boolean | null;
}

interface MaintLayer {
  name: string;
  source: string;
  kind: string;
  cache_file: string;
  max_age_days: number;
  refresh_every_days: number | null;
  exists: boolean;
  age_days: number | null;
  expires_in_days: number | null;
  size_mb: number | null;
  state: "fresh" | "due" | "expiring" | "expired" | "missing";
  credentials_ok: boolean;
  next_refresh: string | null;
}

interface ChainStep {
  module: string;
  script: string | null;
  dataset?: string | null;
  runtime_note?: string | null;
  status?: "pending" | "running" | "complete" | "error" | "skipped";
  run_id?: string | null;
  error?: string | null;
}

interface MaintAlert {
  level: "error" | "warning" | "info";
  source: string;
  kind: string;
  message: string;
  // pipeline_behind only (pipeline_reruns.annotate)
  chain?: ChainStep[];
  fingerprint?: string;
  rerun?: boolean;
  last_failure?: { chain_id: string; module: string; error: string; at: string } | null;
}

interface ChainRun {
  chain_id: string;
  status: "running" | "complete" | "error" | "interrupted";
  approved_by: string;
  approved_by_name?: string;
  approved_at: string;
  finished_at: string | null;
  error: string | null;
  trigger_sources: string[];
  steps: ChainStep[];
  current: number;
}

interface RerunSummary {
  sources: string[];
  chain: ChainStep[];
  dismissed: { source: string; action: string; by: string | null; at: string | null; message: string }[];
  chain_run: ChainRun | null;
  can_approve: boolean;
  error?: string;
}

interface MaintSource {
  name: string;
  label: string;
  group: string;
  mode: "scheduled" | "monitor" | "licensed" | "suspended";
  enabled?: boolean;
  exists?: boolean;
  age_days?: number | null;
  modified?: string | null;
  ttl_days?: number | null;
  refresh_every_days?: number | null;
  warn_after_days?: number | null;
  credentials_ok?: boolean;
  last_ok?: string | null;
  last_status?: string | null;
  last_error?: string | null;
  last_changed?: string | null;
  running?: boolean;
  next_run?: string | null;
  upstream?: { ok?: boolean; newer_available?: boolean | null; upstream_updated?: string | null; error?: string; checked?: string } | null;
  licence?: { license_expires?: string; days_to_license_expires?: number; checkin_by?: string } | null;
  notes?: string;
  can_run?: boolean;
  alerts?: MaintAlert[];
  error?: string;
}

interface MaintFetchEvent {
  at: string;
  action: string;
  cache_file: string;
  process: string;
  rows?: number;
  seconds?: number;
  age_days?: number;
  error?: string;
}

export interface MaintStatus {
  scheduler: {
    active: boolean;
    label?: string;
    host?: string;
    pid?: number;
    heartbeat_age_s?: number;
    started_iso?: string;
  };
  this_server: { scheduler_enabled: boolean; child_pid: number | null };
  tasks: MaintTask[];
  layers?: MaintLayer[];
  layers_error?: string;
  recent_fetches?: MaintFetchEvent[];
  outcome_archive?: OutcomeArchiveSummary;
  sources?: MaintSource[];
  sources_error?: string;
  alerts?: MaintAlert[];
  reruns?: RerunSummary;
  office?: OfficeStatus;
  job_worker?: { queue: string; workers: unknown[]; active: number };
  config: { lookahead_days: number; timezone: string };
}

/** wcc_viewer/office_backend.status(): which engine serves Excel/Word jobs. */
interface OfficeStatus {
  error?: string;
  force_libreoffice?: boolean;
  bridge?: {
    configured: boolean;
    host?: string;
    state?: string;
    circuit_open_for_s?: number;
    last_error?: string | null;
    office?: { excel?: boolean; word?: boolean; version?: string } | null;
  };
  last_served?: Record<string, { backend: string | null; at: number; elapsed_s: number; note?: string }>;
}

export function officeSummary(o: OfficeStatus | undefined): string {
  if (!o) return "";
  if (o.error) return `Office engines: status unavailable (${o.error}).`;
  const b = o.bridge;
  let text = "Office engines (cost-workbook recalculation, Page view): ";
  if (o.force_libreoffice) text += "LibreOffice forced (WCC_FORCE_LIBREOFFICE). ";
  else if (!b?.configured) text += "no Office bridge configured (local Word/Excel if installed, else LibreOffice). ";
  else {
    text += `Office bridge ${b.host ?? ""} is ${b.state ?? "unknown"}`;
    if (b.circuit_open_for_s) text += ` (retrying in ${b.circuit_open_for_s}s)`;
    if (b.office) text += `, Excel ${b.office.excel ? "yes" : "no"}, Word ${b.office.word ? "yes" : "no"}`;
    text += b.state === "up" ? ". " : `; LibreOffice is used meanwhile${b.last_error ? `. Last error: ${b.last_error}` : ""}. `;
  }
  const served = Object.entries(o.last_served ?? {})
    .map(([op, x]) => `${op} → ${x.backend ?? "none"}${x.note ? ` (${x.note})` : ""}`);
  if (served.length) text += `Last served: ${served.join(", ")}.`;
  return text;
}

let statusCache: MaintStatus | null = null;
let loading = false;
// A load has been attempted (even one that failed). Without it a failed load
// left statusCache null, every listener's redraw asked again, and the failure
// re-triggered itself without end (row #30: hundreds of 404s a minute).
let attempted = false;
let showAllLayers = false;
let showInfoAlerts = false;
let hostEl: HTMLElement | null = null;

const fmtTime = (iso: string | null | undefined): string => (iso ? new Date(iso).toLocaleString() : "–");

function summariseResult(task: MaintTask): string {
  const r = task.last_result;
  if (task.last_error) return task.last_error;
  if (!r) return "";
  if (task.name === "live_jobs") {
    return `${r.in_feed ?? "?"} jobs in feed, ${r.new ?? 0} new, ${r.updated ?? 0} changed (cache ${r.cache_total ?? "?"})`;
  }
  const refreshed = Array.isArray(r.refreshed) ? r.refreshed.length : 0;
  const failed = Array.isArray(r.failed) ? r.failed.length : 0;
  const skipped = Array.isArray(r.skipped) ? r.skipped.length : 0;
  const checks = Array.isArray(r.upstream_checks) ? `, ${r.upstream_checks.length} upstream checks` : "";
  return `${refreshed} refreshed, ${failed} failed, ${skipped} skipped${checks}`;
}

const fmtDays = (d: number | null | undefined): string => (d == null ? "–" : d < 10 ? d.toFixed(1) : d.toFixed(0));

function sourceCadence(s: MaintSource): string {
  if (s.mode === "scheduled") {
    if (s.enabled === false) return "disabled";
    return s.refresh_every_days != null ? `every ${s.refresh_every_days} d${s.ttl_days ? ` (TTL ${s.ttl_days} d)` : ""}` : "not configured";
  }
  if (s.mode === "licensed") {
    const l = s.licence;
    return l?.license_expires ? `licence to ${l.license_expires} (${l.days_to_license_expires} d)` : "licensed";
  }
  if (s.mode === "suspended") return "suspended – never refreshed";
  return s.warn_after_days != null ? `manual; review after ${s.warn_after_days} d` : "manual / static";
}

function sourceStatus(s: MaintSource): string {
  if (s.error) return s.error;
  const parts: string[] = [];
  if (s.running) parts.push("running…");
  if (s.mode === "scheduled") {
    parts.push(s.last_status ? `${s.last_status}${s.last_ok ? `, last ok ${fmtTime(s.last_ok)}` : ""}` : "not refreshed by the scheduler yet");
    if (s.last_status === "error" && s.last_error) parts.push(s.last_error);
    if (s.last_changed) parts.push(`content changed ${fmtTime(s.last_changed)}`);
  }
  const u = s.upstream;
  if (u) {
    if (u.ok) parts.push(u.newer_available ? `NEWER upstream (${u.upstream_updated ?? "?"})` : `upstream unchanged (checked ${fmtTime(u.checked)})`);
    else if (u.error) parts.push(`upstream check: ${u.error}`);
  }
  if (s.exists === false) parts.push("not found on this server");
  return parts.join(" · ");
}

const statusListeners = new Set<() => void>();

async function load(): Promise<void> {
  if (loading) return;
  loading = true;
  attempted = true;
  try {
    statusCache = await getJson<MaintStatus>("/api/admin/maintenance");
  } catch (err) {
    showToast(`Couldn't load maintenance status: ${(err as Error).message}`);
  } finally {
    loading = false;
    if (hostEl) draw(hostEl);
    statusListeners.forEach((fn) => fn());
  }
}

/** The shared status payload for the Diagnostics tab: `onChange` runs after
 * every (re)load; `force` fetches a fresh copy. Returns what is cached now.
 * Without `force` it fetches at most once, so a failed load is not retried by
 * the redraw it triggers. */
export function loadMaintenanceStatus(onChange: () => void, force = false): MaintStatus | null {
  statusListeners.add(onChange);
  if (force || !attempted) void load();
  return statusCache;
}

async function runNow(task: string, layer?: string, source?: string): Promise<void> {
  const what = layer ?? source ?? task;
  try {
    const res = await postJson<{ via: string }>("/api/admin/maintenance/run", { task, layer, source });
    showToast(`${what}: queued (${res.via}). Refresh the status in a minute.`);
    window.setTimeout(() => void load(), 4000);
  } catch (err) {
    showToast(`Couldn't start ${what}: ${(err as Error).message}`);
  }
}

// ---------------------------------------------------------------------------
// Suggested re-runs
// ---------------------------------------------------------------------------

const chainText = (steps: ChainStep[]): string => steps.map((s) => s.module).join(" → ");

let progressOpen = false;

function stepLine(s: ChainStep): string {
  const st = s.status ?? "pending";
  const label = st === "complete" ? "done" : st === "running" ? "running…" : st;
  return `Module ${s.module} (${s.script ?? "?"}) — ${label}${s.error ? `: ${s.error}` : ""}`;
}

/** In-page confirm (not window.confirm): lists every module and warns that
 * the run rewrites shared data/interim for everyone. */
function confirmRerun(steps: ChainStep[], sources: string[]): Promise<boolean> {
  return new Promise((resolve) => {
    const back = overlay();
    const box = modalBox();
    box.append(
      el("h2", {}, "Approve pipeline re-run?"),
      el("p", {}, `Triggered by: ${sources.join(", ")}. These ${steps.length} module(s) will run one after another:`),
    );
    const ol = el("ol");
    for (const s of steps) {
      ol.append(el("li", {}, `Module ${s.module} (${s.script ?? "?"})${s.runtime_note ? ` — approx. ${s.runtime_note}` : ""}`));
    }
    box.append(ol);
    box.append(
      el(
        "p",
        { className: "doc-viewer-error" },
        "This overwrites the shared pipeline outputs in data/interim (and module 10's deliverables) for ALL users. " +
          "A failed step stops the chain. Modules-tab runs that write shared data are refused until it finishes.",
      ),
    );
    const row = el("div");
    row.style.display = "flex";
    row.style.gap = "8px";
    row.style.justifyContent = "flex-end";
    const cancel = el("button", {}, "Cancel");
    const ok = el("button", {}, "Approve and run");
    cancel.addEventListener("click", () => {
      back.remove();
      resolve(false);
    });
    ok.addEventListener("click", () => {
      back.remove();
      resolve(true);
    });
    row.append(cancel, ok);
    box.append(row);
    back.append(box);
    document.body.append(back);
  });
}

/** Live progress for the running (or last) chain: polls the chain state and
 * streams the current step's ordinary module-run log. Closing the dialog
 * does not stop the chain — it runs on the server. */
function showChainProgress(): void {
  if (progressOpen) return;
  progressOpen = true;
  const back = overlay();
  const box = modalBox();
  box.style.maxWidth = "640px";
  const title = el("h2", {}, "Pipeline re-run");
  const meta = el("p", { className: "caption" });
  const rows = el("div");
  const logEl = document.createElement("pre");
  logEl.style.maxHeight = "240px";
  logEl.style.overflowY = "auto";
  logEl.style.fontSize = "11px";
  logEl.style.marginTop = "12px";
  const close = el("button", {}, "Close (the chain keeps running)");
  box.append(title, meta, rows, logEl, close);
  back.append(box);
  document.body.append(back);

  let streamedRun: string | null = null;
  let stopStream: (() => void) | null = null;
  let timer: number | undefined;
  close.addEventListener("click", () => {
    progressOpen = false;
    if (timer !== undefined) window.clearTimeout(timer);
    stopStream?.();
    back.remove();
    void load();
  });

  const tick = async (): Promise<void> => {
    let ch: ChainRun | null = null;
    try {
      ch = (await getJson<{ chain_run: ChainRun | null }>("/api/admin/maintenance/reruns/chain")).chain_run;
    } catch (err) {
      meta.textContent = `Couldn't read progress: ${(err as Error).message}`;
    }
    if (!progressOpen) return;
    if (ch) {
      meta.textContent =
        `Chain ${ch.chain_id}, approved by ${ch.approved_by_name ?? ch.approved_by} at ${fmtTime(ch.approved_at)} ` +
        `(${ch.trigger_sources.join(", ")}). Status: ${ch.status}${ch.error ? ` — ${ch.error}` : ""}`;
      rows.innerHTML = "";
      for (const s of ch.steps) {
        rows.append(el("div", { className: s.status === "error" ? "doc-viewer-error" : s.status === "running" ? "" : "caption" }, stepLine(s)));
      }
      const cur = ch.steps.find((s) => s.status === "running" && s.run_id);
      if (cur?.run_id && cur.run_id !== streamedRun) {
        stopStream?.();
        streamedRun = cur.run_id;
        logEl.textContent += `--- Module ${cur.module} (run ${cur.run_id}) ---\n`;
        stopStream = streamRun(
          cur.run_id,
          (line) => {
            logEl.textContent += line + "\n";
            logEl.scrollTop = logEl.scrollHeight;
          },
          () => undefined,
        );
      }
      if (ch.status !== "running") {
        close.textContent = "Close";
        title.textContent = ch.status === "complete" ? "Pipeline re-run finished" : "Pipeline re-run stopped";
        void load();
        return;
      }
    }
    timer = window.setTimeout(() => void tick(), 2000);
  };
  void tick();
}

async function approveRerun(sources: string[], steps: ChainStep[]): Promise<void> {
  if (!(await confirmRerun(steps, sources))) return;
  try {
    await postJson("/api/admin/maintenance/reruns/approve", {
      sources,
      expected_modules: steps.map((s) => s.module),
    });
    showToast("Pipeline re-run approved and started.");
    showChainProgress();
  } catch (err) {
    showToast(`Couldn't start the re-run: ${(err as Error).message}`);
    void load();
  }
}

async function dismissRerun(a: MaintAlert): Promise<void> {
  try {
    await postJson("/api/admin/maintenance/reruns/dismiss", { source: a.source, fingerprint: a.fingerprint ?? null });
    showToast(`${a.source}: dismissed until its content changes again.`);
  } catch (err) {
    showToast(`Couldn't dismiss: ${(err as Error).message}`);
  }
  void load();
}

/** Chain line + Approve / Dismiss buttons for one pipeline-behind notice. */
function rerunControls(a: MaintAlert, r: RerunSummary | undefined): HTMLElement {
  const wrap = el("div", { className: "caption" });
  const busy = r?.chain_run?.status === "running";
  const allowed = !!r?.can_approve;
  const why = !allowed ? "Needs platform_admin" : busy ? "A re-run is in progress" : "";
  if (a.chain && a.chain.length) {
    wrap.append(document.createTextNode(`Suggested re-run: ${chainText(a.chain)} `));
    const approve = el("button", { disabled: !allowed || busy, title: why }, "Approve re-run");
    approve.addEventListener("click", () => void approveRerun([a.source], a.chain ?? []));
    wrap.append(approve, document.createTextNode(" "));
  } else {
    wrap.append(document.createTextNode("No re-run needed for scoring (context only). "));
  }
  const dismiss = el("button", { disabled: !allowed, title: allowed ? "Hide until the content changes again" : why }, "Dismiss");
  dismiss.addEventListener("click", () => void dismissRerun(a));
  wrap.append(dismiss);
  if (a.last_failure) {
    wrap.append(
      el(
        "div",
        { className: "doc-viewer-error" },
        `Last approved re-run (${a.last_failure.chain_id}) failed at module ${a.last_failure.module}: ${a.last_failure.error}`,
      ),
    );
  }
  return wrap;
}

function drawReruns(root: HTMLElement, alerts: MaintAlert[], r: RerunSummary | undefined): void {
  const behind = alerts.filter((a) => a.kind === "pipeline_behind" && a.chain !== undefined);
  root.append(el("h3", {}, `Suggested re-runs (${behind.length})`));
  root.append(
    el(
      "p",
      { className: "caption" },
      "A scheduled refresh changed data that pipeline outputs were built from. Nothing re-runs automatically: " +
        "an approved chain runs module by module (logs as usual), and the notice clears once it completes.",
    ),
  );
  if (r?.error) root.append(el("p", { className: "doc-viewer-error" }, r.error));
  const ch = r?.chain_run;
  if (ch) {
    const bad = ch.status === "error" || ch.status === "interrupted";
    const line = el("p", { className: bad ? "doc-viewer-error" : "caption" });
    const step = ch.steps[ch.current];
    const who = ch.approved_by_name ?? ch.approved_by;
    line.textContent =
      ch.status === "running"
        ? `Re-run ${ch.chain_id} in progress (approved by ${who}): step ${ch.current + 1}/${ch.steps.length}, module ${step?.module ?? "?"}. `
        : `Last re-run ${ch.chain_id} (${chainText(ch.steps)}), approved by ${who} ${fmtTime(ch.approved_at)}: ${ch.status}${ch.error ? ` — ${ch.error}` : ""}. `;
    const view = el("button", {}, ch.status === "running" ? "View progress" : "Details");
    view.addEventListener("click", () => showChainProgress());
    line.append(view);
    root.append(line);
  }
  if (behind.length === 0) {
    root.append(el("p", { className: "caption" }, "The pipeline is up to date with every refreshed source."));
  } else {
    const ul = el("ul");
    for (const a of behind) {
      const li = el("li", { className: a.level === "warning" ? "" : "caption" });
      li.append(document.createTextNode(`${a.source}: ${a.message}`), rerunControls(a, r));
      ul.append(li);
    }
    root.append(ul);
    const actionable = behind.filter((a) => a.chain && a.chain.length);
    if (actionable.length > 1 && r && r.chain.length) {
      const all = el("div", { className: "caption" });
      const busy = r.chain_run?.status === "running";
      all.append(document.createTextNode(`All together: ${chainText(r.chain)} `));
      const btn = el("button", { disabled: !r.can_approve || busy }, `Approve combined re-run (${r.chain.length} modules)`);
      btn.addEventListener("click", () => void approveRerun(r.sources, r.chain));
      all.append(btn);
      root.append(all);
    }
  }
  if (r?.dismissed.length) {
    const det = el("details");
    det.append(el("summary", { className: "caption" }, `Dismissed / already handled (${r.dismissed.length})`));
    const ul = el("ul");
    for (const d of r.dismissed) {
      const what = d.action === "rerun" ? "re-run" : "dismissed";
      ul.append(el("li", { className: "caption" }, `${d.source}: ${what} by ${d.by ?? "?"} ${fmtTime(d.at)} — ${d.message}`));
    }
    det.append(ul);
    root.append(det);
  }
}

function drawAlerts(root: HTMLElement, alerts: MaintAlert[]): void {
  const important = alerts.filter((a) => a.level !== "info");
  const info = alerts.filter((a) => a.level === "info");
  root.append(el("h3", {}, `Alerts (${important.length} need attention, ${info.length} notices)`));
  const shown = showInfoAlerts ? alerts : important;
  if (shown.length === 0) {
    root.append(el("p", { className: "caption" }, important.length === 0 ? "Nothing needs attention." : ""));
  } else {
    const ul = el("ul");
    for (const a of shown) {
      const li = el("li", { className: a.level === "error" ? "doc-viewer-error" : a.level === "info" ? "caption" : "" });
      li.textContent = `${a.level.toUpperCase()} · ${a.source}: ${a.message}`;
      if (a.kind === "pipeline_behind" && a.chain !== undefined) li.append(rerunControls(a, statusCache?.reruns));
      ul.append(li);
    }
    root.append(ul);
  }
  if (info.length) {
    const toggle = el("label", { className: "caption" });
    const cb = el("input", { type: "checkbox", checked: showInfoAlerts }) as HTMLInputElement;
    cb.addEventListener("change", () => {
      showInfoAlerts = cb.checked;
      if (hostEl) draw(hostEl);
    });
    toggle.append(cb, document.createTextNode(" Show notices too (licence countdowns, vintage, pipeline-behind hints)"));
    root.append(toggle);
  }
}

function drawSources(root: HTMLElement, sources: MaintSource[], err?: string): void {
  root.append(el("h3", {}, `Other data sources (${sources.length})`));
  root.append(
    el(
      "p",
      { className: "caption" },
      "Caches with their own download code are refreshed by the data_sources task on their own cadence. " +
        "Static, manual, client-supplied and licensed data is never downloaded or replaced automatically — only its age, " +
        "licence and (where cheap) whether a newer version has been published are checked.",
    ),
  );
  if (err) root.append(el("p", { className: "doc-viewer-error" }, err));
  if (!sources.length) return;
  const t = el("table", { className: "detail-table" });
  const h = document.createElement("tr");
  for (const c of ["Source", "Mode", "Cadence", "Age (days)", "Status", "Next run", ""]) h.append(el("th", {}, c));
  t.append(h);
  for (const s of sources) {
    const tr = document.createElement("tr");
    const worst = (s.alerts ?? []).some((a) => a.level === "error")
      ? "doc-viewer-error"
      : (s.alerts ?? []).some((a) => a.level === "warning")
        ? ""
        : "caption";
    const btnCell = el("td");
    if (s.can_run) {
      const label = s.mode === "scheduled" ? "Refresh now" : "Check upstream";
      const btn = el(
        "button",
        { disabled: !!s.running || s.credentials_ok === false, title: s.credentials_ok === false ? "Credentials not set on the server" : "" },
        label,
      );
      btn.addEventListener("click", () => void runNow("data_source", undefined, s.name));
      btnCell.append(btn);
    }
    tr.append(
      el("td", { title: `${s.name} — ${s.notes ?? ""}` }, s.label ?? s.name),
      el("td", { className: "caption" }, `${s.group} · ${s.mode}`),
      el("td", { className: "caption" }, sourceCadence(s)),
      el("td", { className: "caption", title: s.modified ?? "" }, fmtDays(s.age_days)),
      el("td", { className: worst }, sourceStatus(s)),
      el("td", { className: "caption" }, s.next_run ? fmtTime(s.next_run) : "–"),
      btnCell,
    );
    t.append(tr);
  }
  root.append(scrollWrap(t));
}

/** Wide tables scroll sideways inside the sidebar instead of being clipped. */
function scrollWrap(table: HTMLElement): HTMLElement {
  const wrap = el("div", { className: "admin-table-scroll" });
  wrap.append(table);
  return wrap;
}

function draw(root: HTMLElement): void {
  root.innerHTML = "";
  const header = el("div");
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.gap = "0.5rem";
  header.style.flexWrap = "wrap";
  header.append(el("h2", {}, "Data maintenance"));
  const reload = el("button", {}, loading ? "Loading…" : "Refresh status");
  reload.addEventListener("click", () => void load());
  header.append(reload);
  root.append(header);

  const s = statusCache;
  if (!s) {
    root.append(el("p", { className: "caption" }, "Loading maintenance status…"));
    return;
  }

  const sch = s.scheduler;
  root.append(
    el(
      "p",
      { className: "caption" },
      sch.active
        ? `Scheduler running on ${sch.label ?? sch.host} (pid ${sch.pid}), heartbeat ${sch.heartbeat_age_s}s ago. ` +
            `This server ${s.this_server.scheduler_enabled ? "starts a scheduler too (it idles while another holds the lease)" : "does not start one"}.`
        : "No scheduler is running (no live leader lease in data/maintenance/). Buttons below start a one-off " +
            "maintenance process instead. The public copy starts the scheduler when WCC_MAINTENANCE=1.",
    ),
  );

  drawReruns(root, s.alerts ?? [], s.reruns);
  drawAlerts(root, s.alerts ?? []);

  // --- tasks ---
  const tt = el("table", { className: "detail-table" });
  const th = document.createElement("tr");
  for (const h of ["Task", "Schedule", "Last run", "Result", "Next run", ""]) th.append(el("th", {}, h));
  tt.append(th);
  for (const t of s.tasks) {
    const tr = document.createElement("tr");
    const status = t.running ? "running…" : t.interrupted ? "interrupted" : (t.last_status ?? "never run");
    const resultCell = el("td", { className: t.last_status === "error" ? "doc-viewer-error" : "caption" });
    resultCell.textContent = `${status}${t.last_duration_s != null && !t.running ? ` (${t.last_duration_s}s)` : ""}. ${summariseResult(t)}`;
    const btnCell = el("td");
    if (t.name !== "gis_layer" && t.name !== "data_source") {
      const btn = el("button", { disabled: t.running }, "Run now");
      btn.addEventListener("click", () => void runNow(t.name));
      btnCell.append(btn);
    }
    const nextText = t.next_run
      ? fmtTime(t.next_run)
      : sch.active && t.leader_supports === false && t.schedule !== "on demand"
        ? "not scheduled (leader runs an older build)"
        : "–";
    tr.append(
      el("td", {}, t.name),
      el("td", { className: "caption" }, t.enabled ? t.schedule : `${t.schedule} (disabled)`),
      el("td", { className: "caption" }, fmtTime(t.last_started)),
      resultCell,
      el("td", { className: "caption" }, nextText),
      btnCell,
    );
    tt.append(tr);
  }
  root.append(scrollWrap(tt));

  drawSources(root, s.sources ?? [], s.sources_error);

  // --- layers ---
  const layers = s.layers ?? [];
  const order = { expired: 0, expiring: 1, due: 2, missing: 3, fresh: 4 } as const;
  const sorted = [...layers].sort(
    (a, b) => order[a.state] - order[b.state] || (a.expires_in_days ?? -1e9) - (b.expires_in_days ?? -1e9),
  );
  const attention = sorted.filter((l) => l.state === "expired" || l.state === "expiring" || l.state === "due");
  const h3 = el("h3", {}, `GIS layer caches (${attention.length} of ${layers.length} expired, expiring within ${s.config.lookahead_days} days or due)`);
  root.append(h3);
  if (s.layers_error) root.append(el("p", { className: "doc-viewer-error" }, s.layers_error));
  const toggle = el("label", { className: "caption" });
  const cb = el("input", { type: "checkbox", checked: showAllLayers }) as HTMLInputElement;
  cb.addEventListener("change", () => {
    showAllLayers = cb.checked;
    draw(root);
  });
  toggle.append(cb, document.createTextNode(" Show fresh and missing layers too"));
  root.append(toggle);

  const shown = showAllLayers ? sorted : attention;
  if (shown.length === 0) {
    root.append(el("p", { className: "caption" }, "Every cached layer is fresh."));
  } else {
    const lt = el("table", { className: "detail-table" });
    const lh = document.createElement("tr");
    for (const h of ["Layer", "State", "Age (days)", "Max age", "Expires in", "Size MB", "Next refresh", ""]) lh.append(el("th", {}, h));
    lt.append(lh);
    for (const l of shown) {
      const tr = document.createElement("tr");
      const btnCell = el("td");
      const btn = el("button", { disabled: !l.credentials_ok, title: l.credentials_ok ? "" : "Credentials not set on the server" }, "Refresh now");
      btn.addEventListener("click", () => void runNow("gis_layer", l.name));
      btnCell.append(btn);
      const stateCell = el("td", { className: l.state === "expired" ? "doc-viewer-error" : "caption" }, l.state);
      tr.append(
        el("td", { title: `${l.cache_file} (${l.source}, ${l.kind})` }, l.name),
        stateCell,
        el("td", { className: "caption" }, l.age_days != null ? l.age_days.toFixed(1) : "–"),
        el("td", { className: "caption" }, l.refresh_every_days ? `${l.max_age_days} (every ${l.refresh_every_days})` : String(l.max_age_days)),
        el("td", { className: "caption" }, l.expires_in_days != null ? l.expires_in_days.toFixed(1) : "–"),
        el("td", { className: "caption" }, l.size_mb != null ? l.size_mb.toFixed(1) : "–"),
        el("td", { className: "caption" }, l.next_refresh ? fmtTime(l.next_refresh) : l.state === "missing" ? "on demand" : "–"),
        btnCell,
      );
      lt.append(tr);
    }
    root.append(scrollWrap(lt));
  }

  // --- outcome-data archive (outcome_archive.py, flow plan stage A) ---
  const oa = s.outcome_archive;
  if (oa && !oa.error) {
    root.append(el("h3", {}, `Outcome-data archive (${fmtBytes(oa.total_bytes)})`));
    root.append(el("p", { className: "caption" },
      `Timestamped "before" snapshots for retrospectives in ${oa.root}: live jobs daily, raw Hilltop series weekly (incremental), DMA / Maximo extracts when they change. Retention: ${oa.retention}. Run it now with the outcome_archive task above.`));
    const at = el("table", { className: "detail-table archive-table" });
    const ah = document.createElement("tr");
    for (const h of ["Source", "Snapshots", "Latest", "Size", "Last run", "Series / fingerprint"]) ah.append(el("th", {}, h));
    at.append(ah);
    for (const r of oa.sources) {
      const tr = document.createElement("tr");
      tr.append(
        el("td", {}, r.source),
        el("td", {}, String(r.snapshots)),
        el("td", { className: "caption" }, r.latest ?? "–"),
        el("td", { className: "caption" }, fmtBytes(r.bytes)),
        el("td", { className: "caption" }, r.last_run ? fmtTime(r.last_run) : "–"),
        el("td", { className: "caption" }, r.series != null ? `${r.series} series` : r.fingerprint ?? "–"),
      );
      at.append(tr);
    }
    root.append(scrollWrap(at));
  } else if (oa?.error) {
    root.append(el("p", { className: "caption" }, `Outcome-data archive status unavailable: ${oa.error}`));
  }

  // --- recent fetch activity (any process: scheduler, pipeline modules, viewer) ---
  const ev = s.recent_fetches ?? [];
  if (ev.length) {
    const det = el("details");
    det.append(el("summary", { className: "caption" }, `Recent GIS download activity (${ev.length})`));
    const et = el("table", { className: "detail-table" });
    const eh = document.createElement("tr");
    for (const h of ["When", "Action", "Layer file", "By", "Detail"]) eh.append(el("th", {}, h));
    et.append(eh);
    for (const e of ev) {
      const tr = document.createElement("tr");
      const detail = e.error ?? (e.rows != null ? `${e.rows} rows, ${e.seconds}s` : e.age_days != null ? `cache ${e.age_days} days old` : "");
      tr.append(
        el("td", { className: "caption" }, fmtTime(e.at)),
        el("td", {}, e.action),
        el("td", { className: "caption" }, e.cache_file),
        el("td", { className: "caption" }, e.process),
        el("td", { className: "caption" }, detail),
      );
      et.append(tr);
    }
    det.append(scrollWrap(et));
    root.append(det);
  }
}

interface OutcomeArchiveSummary {
  root: string;
  total_bytes: number;
  retention: string;
  error?: string;
  sources: { source: string; snapshots: number; latest: string | null; bytes: number; last_run: string | null; fingerprint: string | null; series: number | null }[];
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Renders into the Data maintenance tab; fetches status on first render. */
export function renderMaintenanceSection(root: HTMLElement): void {
  const host = el("div");
  root.append(host);
  hostEl = host;
  draw(host);
  if (!attempted) void load();
}
